/**
 * AdminContacts — Base unifiée des personnes physiques (RGPD-ready).
 *
 * Fonctionnalités :
 * - Liste paginée 50/page, search, filtres (source/pôle/tag), tri colonnes
 * - Création manuelle, édition inline modale
 * - Archiver / désarchiver / supprimer définitivement
 * - Import / Export CSV
 * - Export RGPD complet (JSON) par contact
 * - Synchronisation depuis sources existantes
 */
import { useEffect, useRef, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail, getAuthToken } from "@/lib/api";
import { useAdminTable } from "@/lib/useAdminTable";
import { AdminTableToolbar, AdminTablePager, SortHeader } from "@/components/admin/AdminTableHelpers";
import { POLES, poleLabel } from "@/lib/poles";
import CompanyAutocomplete from "@/components/CompanyAutocomplete";
import { toast } from "sonner";
import {
  UserPlus, Mail, Phone, Building2, Trash2, Archive, ArchiveRestore,
  Download, Upload, RefreshCw, Edit3, Tag, Shield, X, Save, Crown, Link2, CheckSquare, Square, Users2, Send, Linkedin, Key, KeyRound, FileText,
} from "lucide-react";
import CountrySelect from "@/components/CountrySelect";
import { langForCountry } from "@/lib/countries";
import BulkEmailModal from "@/components/admin/BulkEmailModal";

const LANG_FLAGS = { fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", it: "🇮🇹", pt: "🇵🇹" };
const LANG_LABELS = { fr: "Français", en: "English", es: "Español", it: "Italiano", pt: "Português" };

function ContactLangFlag({ contact }) {
  // Resolve lang from explicit field or billing_country fallback (matches backend logic)
  const lang = contact.lang || langForCountry(contact.billing_country || contact.country) || "fr";
  return (
    <span title={`Langue d'envoi : ${LANG_LABELS[lang] || lang.toUpperCase()}`} className="grid h-7 w-7 place-items-center rounded-full bg-neutral-50 text-base ring-1 ring-neutral-200" data-testid={`contact-lang-${contact.id}`}>
      {LANG_FLAGS[lang] || "🌐"}
    </span>
  );
}

const SOURCES = [
  { value: "manual", label: "Manuel" },
  { value: "member", label: "Membre" },
  { value: "prospect", label: "Prospect" },
  { value: "event", label: "Événement" },
  { value: "applicant", label: "Candidat" },
  { value: "import", label: "Import CSV" },
];
const VIS_LEVELS = [
  { value: "admin_only", label: "Admin uniquement" },
  { value: "members", label: "Membres" },
  { value: "public", label: "Public" },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.value, s.label]));
const SOURCE_COLOR = {
  manual: "bg-neutral-100 text-neutral-700",
  member: "bg-emerald-100 text-emerald-700",
  prospect: "bg-blue-100 text-blue-700",
  event: "bg-amber-100 text-amber-700",
  applicant: "bg-purple-100 text-purple-700",
  import: "bg-pink-100 text-pink-700",
};

const EMPTY_CONTACT = {
  first_name: "", last_name: "", email: "", phone: "", job_title: "", linkedin: "", calendar_link: "",
  company_id: null, company_name: "", pole: "", tags: [],
  source: "manual", notes: "", consent_source: "", legal_basis: "",
};

// Sprint 92n — Resolve a friendly label for a tag by looking it up in segmentGroups.
// Falls back to the raw value (nicely rendered).
function tagLabelFor(tag, segmentGroups = [], extraTags = []) {
  for (const g of segmentGroups || []) {
    for (const v of g.values || []) {
      if (v.value === tag) return v.label || v.value;
    }
  }
  if ((extraTags || []).includes(tag)) return tag;
  return tag;
}

const LEGAL_BASIS_LABEL = {
  contract: { label: "Contrat", emoji: "📄", color: "bg-blue-50 text-blue-700 border-blue-200" },
  consent: { label: "Consentement", emoji: "✅", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  execution_contract: { label: "Exécution contrat", emoji: "⚖️", color: "bg-purple-50 text-purple-700 border-purple-200" },
  legal_obligation: { label: "Obligation légale", emoji: "📜", color: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function AdminContacts() {
  const table = useAdminTable("/admin/contacts", { sort: "created_at", order: "desc" });
  const [stats, setStats] = useState({ total: 0, archived: 0, by_source: [] });
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const fileInput = useRef(null);
  const [converting, setConverting] = useState(null);
  const [convertingToCandidate, setConvertingToCandidate] = useState(null);
  const [linking, setLinking] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [dupGroups, setDupGroups] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState({ open: false, contact: null, password: "", notify: true });
  const [segmentGroups, setSegmentGroups] = useState([]); // Sprint 86 — segment filter
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/admin/contacts/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [table.total]);

  useEffect(() => {
    api.get("/admin/contacts/segments").then((r) => setSegmentGroups(r.data?.groups || [])).catch(() => {});
  }, []);

  const archive = async (c) => {
    if (!window.confirm(`Archiver le contact ${c.first_name} ${c.last_name} ?`)) return;
    await api.post(`/admin/contacts/${c.id}/archive`);
    toast.success("Contact archivé");
    table.reload();
  };

  const loadDuplicates = async () => {
    try {
      const { data } = await api.get("/admin/contact-duplicates");
      setDupGroups(data);
      setDupOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const mergeDuplicates = async (group, primaryId) => {
    const dupIds = group.contacts.map((c) => c.id).filter((id) => id !== primaryId);
    if (dupIds.length === 0) return;
    if (!window.confirm(`Fusionner ${dupIds.length} doublon(s) dans le contact sélectionné ? Les doublons seront archivés.`)) return;
    try {
      await api.post("/admin/contact-merge", { primary_id: primaryId, duplicate_ids: dupIds });
      toast.success(`${dupIds.length} doublon(s) fusionné(s)`);
      loadDuplicates();
      table.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const unarchive = async (c) => {
    await api.post(`/admin/contacts/${c.id}/unarchive`);
    toast.success("Contact restauré");
    table.reload();
  };
  const remove = async (c) => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ${c.first_name} ${c.last_name} ?\n\nCette action est IRRÉVERSIBLE.\n\nUtilisez plutôt "Archiver" si vous voulez juste le masquer.`)) return;
    await api.delete(`/admin/contacts/${c.id}`);
    toast.success("Contact supprimé");
    table.reload();
  };
  const rgpdExport = async (c) => {
    try {
      const r = await api.get(`/admin/contacts/${c.id}/rgpd-export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rgpd-${c.email || c.id}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export RGPD téléchargé");
    } catch (err) { toast.error("Erreur export RGPD"); }
  };
  const sendProfileInvite = async (c) => {
    if (!c.email) { toast.error("Ce contact n'a pas d'email."); return; }
    if (!window.confirm(`Envoyer à ${c.first_name} ${c.last_name} (${c.email}) un email l'invitant à compléter son profil et créer son mot de passe ?`)) return;
    try {
      const r = await api.post(`/admin/contacts/${c.id}/send-profile-invite`);
      toast.success(`Invitation envoyée à ${r.data.to}`);
      table.reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };
  const convertToCandidate = async (c) => {
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email;
    if (!window.confirm(`Convertir ${name} en candidat ?\n\nUne nouvelle candidature "under_review" sera créée dans /admin/applications. Aucun email ni paiement n'est déclenché.`)) return;
    setConvertingToCandidate(c.id);
    try {
      const r = await api.post(`/admin/contacts/${c.id}/convert-to-candidate`);
      if (r.data.already) {
        toast.info("Ce contact est déjà converti — j'ouvre la fiche candidat.");
      } else {
        toast.success("Candidature créée ✅ — redirection vers la fiche");
      }
      table.reload();
      window.location.href = `/admin/applications/${r.data.application_id}`;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de conversion");
    } finally {
      setConvertingToCandidate(null);
    }
  };
  const sendPasswordReset = async (c) => {
    if (!c.email) { toast.error("Ce contact n'a pas d'email."); return; }
    if (!window.confirm(`Envoyer à ${c.first_name} ${c.last_name} (${c.email}) un lien de réinitialisation de mot de passe (valable 1 heure) ?`)) return;
    try {
      const r = await api.post(`/admin/contacts/${c.id}/send-password-reset`);
      toast.success(`${r.data.user_created ? "Compte créé · " : ""}Lien envoyé à ${r.data.to}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };
  const submitManualPassword = async (e) => {
    e.preventDefault();
    if (!passwordModal.contact || passwordModal.password.length < 8) return;
    try {
      const r = await api.post(`/admin/contacts/${passwordModal.contact.id}/set-password`, {
        password: passwordModal.password,
        notify: passwordModal.notify,
      });
      toast.success(`Mot de passe ${r.data.user_created ? "défini sur nouveau compte" : "mis à jour"} pour ${r.data.to}${r.data.notified ? " — email envoyé" : ""}`);
      setPasswordModal({ open: false, contact: null, password: "", notify: true });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };
  const syncFromSources = async () => {
    if (!window.confirm("Synchroniser la base contacts depuis :\n• Membres existants\n• Prospects CRM\n• Participants événements\n• Candidatures\n\n(Idempotent — les doublons sont ignorés)")) return;
    try {
      const r = await api.post("/admin/contacts/sync");
      const s = r.data.stats;
      toast.success(`Sync OK : +${s.members} membres, +${s.prospects} prospects, +${s.participants} participants, +${s.applicants} candidats`);
      table.reload();
    } catch (err) { toast.error("Erreur sync"); }
  };
  const reconcileCompanies = async () => {
    if (!window.confirm("Rattacher les contacts orphelins aux entreprises membres existantes ?\n\n" +
      "Le matching se fait par :\n• Nom d'entreprise (exact ou début)\n• Domaine email (hors gmail/yahoo/orange…)\n\n" +
      "Cas typique : Magali-Eve (VINVENTIONS) sera automatiquement rattachée à la fiche VINVENTIONS.")) return;
    try {
      const r = await api.post("/admin/contacts/reconcile-companies");
      const s = r.data.stats;
      toast.success(`Réconciliation OK : ${s.linked} contacts rattachés (${s.skipped} sans match, ${s.total_scanned} scannés)`);
      table.reload();
    } catch (err) { toast.error("Erreur réconciliation"); }
  };
  const exportCsv = () => {
    const params = new URLSearchParams({
      q: table.q || "",
      archived: table.archived,
      ...Object.fromEntries(Object.entries(table.filters).filter(([, v]) => v)),
    });
    const token = getAuthToken();
    fetch(`${BACKEND}/api/admin/contacts/export.csv?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const ct = r.headers.get("Content-Type") || "";
      if (!ct.includes("csv")) throw new Error("Réponse non-CSV");
      return r.blob();
    }).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    }).catch((err) => toast.error("Erreur export : " + err.message));
  };

  const exportSelection = async () => {
    if (selected.size === 0) return;
    const token = getAuthToken();
    try {
      const r = await fetch(`${BACKEND}/api/admin/contacts/export-selection.csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `contacts-selection-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${selected.size} contact(s) exporté(s)`);
      setSelected(new Set());
    } catch (err) { toast.error("Erreur export : " + err.message); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === table.items.length) setSelected(new Set());
    else setSelected(new Set(table.items.map((c) => c.id)));
  };

  // ---------- Bulk emailing (mailshot to selection) ----------
  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
  const [bulkEmail, setBulkEmail] = useState({ open: false, subject: "", html: "", sending: false, template_id: "" });
  const [templates, setTemplates] = useState([]);
  const openBulkEmail = async () => {
    if (selected.size === 0) return;
    try {
      const r = await api.get("/admin/settings/email-templates");
      setTemplates(r.data || []);
    } catch { setTemplates([]); }
    setBulkEmail({ open: true, subject: "", html: "", sending: false, template_id: "" });
  };
  const applyTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (!t) { setBulkEmail((b) => ({ ...b, template_id: "" })); return; }
    setBulkEmail((b) => ({ ...b, template_id: tplId, subject: t.subject || "", html: t.body || "" }));
  };
  const sendBulkEmail = async () => {
    if (!bulkEmail.subject.trim() || !bulkEmail.html.trim()) {
      toast.error("Sujet et message requis");
      return;
    }
    setBulkEmail((b) => ({ ...b, sending: true }));
    try {
      const r = await api.post("/admin/mailshots/send", {
        subject: bulkEmail.subject,
        html: bulkEmail.html,
        title: `Emailing manuel — ${selected.size} contact(s)`,
        filter: { selected_ids: Array.from(selected) },
      });
      toast.success(`Campagne lancée — ${r.data?.recipients_count || selected.size} destinataire(s)`);
      setBulkEmail({ open: false, subject: "", html: "", sending: false, template_id: "" });
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur envoi");
      setBulkEmail((b) => ({ ...b, sending: false }));
    }
  };

  // ---------- Bulk newsletter invites ----------
  const sendNewsletterInvites = async () => {
    if (selected.size === 0) return;
    const intro = window.prompt(`Inviter ${selected.size} contact${selected.size > 1 ? "s" : ""} à s'inscrire à la newsletter ?\n\nL'email d'invitation sera envoyé automatiquement dans la langue de chaque contact (fr/en/es/it/pt/de).\n\nMot d'introduction personnalisé (facultatif) :`, "");
    if (intro === null) return;
    try {
      const r = await api.post("/admin/contacts/newsletter-invite", { contact_ids: Array.from(selected), custom_intro: intro });
      const { sent, skipped, errors } = r.data || {};
      toast.success(`📰 ${sent} invitation${sent > 1 ? "s" : ""} envoyée${sent > 1 ? "s" : ""}${skipped ? ` — ${skipped} ignoré${skipped > 1 ? "s" : ""} (déjà inscrits ou sans email)` : ""}${errors ? ` — ${errors} échec${errors > 1 ? "s" : ""}` : ""}`, { duration: 6000 });
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  // ---------- Bulk delete contacts ----------
  const bulkDeleteContacts = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selected.size} contact${selected.size > 1 ? "s" : ""} ? Cette action est irréversible.`)) return;
    try {
      const r = await api.post("/admin/contacts/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} contact${r.data.deleted > 1 ? "s" : ""} supprimé${r.data.deleted > 1 ? "s" : ""}`);
      setSelected(new Set());
      table.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/admin/contacts/import", fd);
      toast.success(`Import OK : ${r.data.created} contacts créés` + (r.data.total_errors ? ` (${r.data.total_errors} erreurs)` : ""));
      table.reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur import"); }
    e.target.value = "";
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-navy md:text-4xl">Contacts</h1>
            <p className="mt-1 text-sm text-neutral-600">Base unifiée RGPD-ready · {stats.total} contacts actifs · {stats.archived} archivés</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={syncFromSources} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="sync-contacts-btn"><RefreshCw className="h-3.5 w-3.5" /> Synchroniser</button>
            <button onClick={reconcileCompanies} className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 px-4 py-2 text-xs font-bold text-amber-800 hover:bg-amber-100" data-testid="reconcile-companies-btn" title="Rattacher les contacts orphelins aux entreprises membres par nom / domaine email"><Link2 className="h-3.5 w-3.5" /> Rattacher aux entreprises</button>
            <button onClick={() => fileInput.current?.click()} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="import-csv-btn"><Upload className="h-3.5 w-3.5" /> Import CSV</button>
            <input ref={fileInput} type="file" accept=".csv" onChange={importCsv} hidden />
            <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="export-csv-btn"><Download className="h-3.5 w-3.5" /> Export CSV</button>
            <button onClick={loadDuplicates} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-xs font-bold text-amber-800 hover:bg-amber-100" data-testid="find-duplicates-btn"><Users2 className="h-3.5 w-3.5" /> Doublons</button>
            <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="new-contact-btn"><UserPlus className="h-3.5 w-3.5" /> Nouveau contact</button>
          </div>
        </div>

        {/* Source breakdown stats */}
        <div className="flex flex-wrap gap-2" data-testid="contacts-source-stats">
          {stats.by_source.map((s) => (
            <button key={s.source} onClick={() => table.setFilters({ source: s.source === (table.filters.source || "") ? "" : s.source })} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${SOURCE_COLOR[s.source] || "bg-neutral-100"} ${table.filters.source === s.source ? "ring-2 ring-wine-500" : ""}`}>
              {SOURCE_LABEL[s.source] || s.source} <span className="opacity-70">{s.count}</span>
            </button>
          ))}
        </div>

        {/* Sprint 92v — Quick-tag filter chips: top 10 most-used tags for one-click filtering */}
        {(() => {
          const allTags = segmentGroups.flatMap((g) => (g.values || []).map((v) => ({ ...v, group: g.label })));
          const topTags = allTags.filter((v) => v.count > 0).sort((a, b) => b.count - a.count).slice(0, 10);
          if (topTags.length === 0) return null;
          const currentTag = table.filters.tag || "";
          return (
            <div className="flex flex-wrap items-center gap-2" data-testid="contacts-quick-tags">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                <Tag className="h-3 w-3" /> Filtres rapides :
              </span>
              {topTags.map((t) => (
                <button
                  key={t.value}
                  onClick={() => table.setFilters({ tag: t.value === currentTag ? "" : t.value })}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition ${currentTag === t.value ? "bg-wine-600 text-white ring-2 ring-wine-400" : "bg-white text-neutral-700 border border-neutral-200 hover:border-wine-300 hover:bg-wine-50"}`}
                  data-testid={`quick-tag-${t.value}`}
                  title={t.group}
                >
                  {t.label} <span className={`text-[10px] font-bold ${currentTag === t.value ? "opacity-80" : "opacity-60"}`}>{t.count}</span>
                </button>
              ))}
              {currentTag && (
                <button onClick={() => table.setFilters({ tag: "" })} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-600 hover:bg-neutral-200" data-testid="clear-tag-filter">
                  <X className="h-3 w-3" /> Effacer
                </button>
              )}
            </div>
          );
        })()}

        <AdminTableToolbar
          table={table}
          placeholder="Rechercher nom, email, téléphone, entreprise…"
          filters={[
            { key: "source", label: "Toutes sources", options: SOURCES },
            { key: "pole", label: "Tous pôles", options: POLES.map((p) => ({ value: p.id, label: p.label })) },
            { key: "consent", label: "Tous consents", options: [
              { value: "comm", label: "✅ Comms RGPD" },
              { value: "newsletter", label: "📧 Newsletter" },
            ] },
            { key: "tag", label: "Tous segments", options: segmentGroups.flatMap((g) => (g.values || []).filter((v) => v.count > 0).map((v) => ({ value: v.value, label: `${g.label} · ${v.label} (${v.count})` }))) },
          ]}
        />

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-blue-200 bg-blue-50/60 p-3" data-testid="bulk-actions-bar">
            <div className="text-sm font-bold text-blue-900">
              {selected.size} contact{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setBulkEmailModalOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="bulk-email-auto-btn" title="Envoyer un email avec template + langue auto par destinataire">
                <Send className="h-3.5 w-3.5" /> Email (template auto-langue)
              </button>
              <button onClick={openBulkEmail} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-600" data-testid="bulk-email-btn" title="Envoyer un emailing groupé (compose libre)">
                <Send className="h-3.5 w-3.5" /> Emailing libre
              </button>
              <button onClick={sendNewsletterInvites} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-700" data-testid="bulk-newsletter-invite-btn" title="Envoyer une invitation newsletter (multilingue auto)">
                📰 Inviter à la newsletter
              </button>
              <button onClick={exportSelection} className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-blue-700" data-testid="export-selection-btn">
                <Download className="h-3.5 w-3.5" /> Exporter sélection
              </button>
              <button onClick={bulkDeleteContacts} className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-700" data-testid="bulk-delete-contacts-btn" title="Supprimer définitivement la sélection">
                <Trash2 className="h-3.5 w-3.5" /> Supprimer la sélection
              </button>
              <button onClick={() => setSelected(new Set())} className="rounded-full border border-blue-300 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-50">
                Désélectionner
              </button>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-neutral-100 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <button onClick={toggleSelectAll} className="grid h-5 w-5 place-items-center text-neutral-500 hover:text-wine-600" data-testid="select-all-checkbox" title="Tout sélectionner / désélectionner">
                      {selected.size > 0 && selected.size === table.items.length ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left"><SortHeader table={table} col="last_name" label="Nom" /></th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left"><SortHeader table={table} col="company_name" label="Entreprise" /></th>
                  <th className="px-4 py-3 text-left">Source</th>
                  <th className="px-4 py-3 text-left">Consents</th>
                  <th className="px-4 py-3 text-left"><SortHeader table={table} col="created_at" label="Créé le" /></th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="contacts-tbody">
                {table.items.map((c) => (
                  <tr key={c.id} className={`border-t border-neutral-100 hover:bg-wine-50/20 ${selected.has(c.id) ? "bg-blue-50/40" : ""}`} data-testid={`contact-row-${c.id}`}>
                    <td className="px-3 py-3">
                      <button onClick={() => toggleSelect(c.id)} className="grid h-5 w-5 place-items-center text-neutral-400 hover:text-wine-600" data-testid={`select-${c.id}`}>
                        {selected.has(c.id) ? <CheckSquare className="h-4 w-4 text-wine-600" /> : <Square className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ContactLangFlag contact={c} />
                        <div>
                          <div className="font-semibold text-neutral-900">{c.first_name} {c.last_name}</div>
                          {c.job_title && <div className="text-xs text-neutral-500">{c.job_title}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      <div className="flex items-center gap-1.5 text-xs"><Mail className="h-3 w-3 text-neutral-400" />{c.email || "—"}</div>
                      {c.phone && <div className="flex items-center gap-1.5 text-xs text-neutral-500"><Phone className="h-3 w-3" />{c.phone}</div>}
                      {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-[#0A66C2] hover:underline" data-testid={`contact-linkedin-${c.id}`}><Linkedin className="h-3 w-3" />LinkedIn</a>}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      <div className="flex items-center gap-1.5 text-xs"><Building2 className="h-3 w-3 text-neutral-400" />{c.company_name || "—"}</div>
                      {c.pole && <span className="mt-1 inline-block rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-semibold text-wine-700">{poleLabel(c.pole)}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${SOURCE_COLOR[c.source] || "bg-neutral-100"}`}>{SOURCE_LABEL[c.source] || c.source}</span>
                      {Array.isArray(c.tags) && c.tags.length > 0 && (
                        <div className="mt-1 flex max-w-[180px] flex-wrap gap-0.5" data-testid={`contact-tags-${c.id}`}>
                          {c.tags.slice(0, 3).map((t) => (
                            <span key={t} className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700" title={t}>{t}</span>
                          ))}
                          {c.tags.length > 3 && <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[9px] text-neutral-500" title={c.tags.slice(3).join(", ")}>+{c.tags.length - 3}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3" data-testid={`contact-consents-${c.id}`}>
                      <div className="flex flex-col gap-1">
                        {c.consent_at ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700" title={`Consentement RGPD le ${new Date(c.consent_at).toLocaleDateString("fr")}${c.consent_source ? " · " + c.consent_source : ""}`}>
                            <Shield className="h-2.5 w-2.5" /> Comms RGPD
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] text-neutral-500" title="Aucun consentement RGPD explicite enregistré">—</span>
                        )}
                        {c.is_newsletter_subscriber ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700" title={`Newsletter${c.newsletter_source ? " · source : " + c.newsletter_source : ""}${c.newsletter_consent_at ? " · " + new Date(c.newsletter_consent_at).toLocaleDateString("fr") : ""}`}>
                            <Mail className="h-2.5 w-2.5" /> Newsletter
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-50 px-2 py-0.5 text-[10px] text-neutral-400">Pas newsletter</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString("fr") : "—"}
                      {c.consent_at && <div className="text-[10px] text-emerald-600" title={c.consent_source}><Shield className="inline h-2.5 w-2.5" /> RGPD ✓</div>}
                      {c.legal_basis && LEGAL_BASIS_LABEL[c.legal_basis] && (
                        <div className={`mt-0.5 inline-flex rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${LEGAL_BASIS_LABEL[c.legal_basis].color}`} title={`Base légale RGPD : ${LEGAL_BASIS_LABEL[c.legal_basis].label}`} data-testid={`legal-basis-${c.id}`}>
                          {LEGAL_BASIS_LABEL[c.legal_basis].emoji} {LEGAL_BASIS_LABEL[c.legal_basis].label}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditing({ ...c })} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Modifier" data-testid={`edit-contact-${c.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setLinking(c)} className={`grid h-7 w-7 place-items-center rounded-lg ${c.linked_prospect_id || c.company_id ? "bg-indigo-50 text-indigo-700" : "text-neutral-500 hover:bg-indigo-50 hover:text-indigo-700"}`} title={c.linked_prospect_id ? "Lié à un prospect" : c.company_id ? "Lié à un membre" : "Lier à prospect/membre"} data-testid={`link-contact-${c.id}`}><Link2 className="h-3.5 w-3.5" /></button>
                        {!c.tags?.includes("member-invited") && c.email && (
                          <button onClick={() => setConverting(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-purple-50 hover:text-purple-700" title="Convertir en membre (fast-track + facturation)" data-testid={`convert-contact-${c.id}`}><Crown className="h-3.5 w-3.5" /></button>
                        )}
                        {c.email && !c.converted_application_id && (
                          <button
                            onClick={() => convertToCandidate(c)}
                            disabled={convertingToCandidate === c.id}
                            className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-wine-50 hover:text-wine-700 disabled:opacity-50"
                            title="Convertir en candidat (dossier à étudier)"
                            data-testid={`convert-contact-candidate-${c.id}`}
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {c.converted_application_id && (
                          <a
                            href={`/admin/applications/${c.converted_application_id}`}
                            className="grid h-7 w-7 place-items-center rounded-lg bg-wine-50 text-wine-700 hover:bg-wine-100"
                            title="Voir la candidature liée"
                            data-testid={`view-linked-application-${c.id}`}
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </a>
                        )}
                        {c.email && (
                          <button onClick={() => sendProfileInvite(c)} className={`grid h-7 w-7 place-items-center rounded-lg ${c.tags?.includes("profile-invited") ? "bg-emerald-50 text-emerald-700" : "text-neutral-500 hover:bg-wine-50 hover:text-wine-700"}`} title={c.tags?.includes("profile-invited") ? "Invitation déjà envoyée — Renvoyer" : "Inviter à compléter son profil"} data-testid={`invite-profile-${c.id}`}><Send className="h-3.5 w-3.5" /></button>
                        )}
                        {c.email && (
                          <button onClick={() => sendPasswordReset(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-sky-50 hover:text-sky-700" title="Envoyer un lien de réinitialisation de mot de passe (1h)" data-testid={`reset-password-${c.id}`}><KeyRound className="h-3.5 w-3.5" /></button>
                        )}
                        {c.email && (
                          <button onClick={() => setPasswordModal({ open: true, contact: c, password: "", notify: true })} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-amber-50 hover:text-amber-700" title="Définir manuellement un mot de passe" data-testid={`set-password-${c.id}`}><Key className="h-3.5 w-3.5" /></button>
                        )}
                        <button onClick={() => rgpdExport(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-emerald-50 hover:text-emerald-700" title="Export RGPD" data-testid={`rgpd-export-${c.id}`}><Shield className="h-3.5 w-3.5" /></button>
                        {c.archived ? (
                          <button onClick={() => unarchive(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-emerald-50 hover:text-emerald-700" title="Restaurer" data-testid={`unarchive-contact-${c.id}`}><ArchiveRestore className="h-3.5 w-3.5" /></button>
                        ) : (
                          <button onClick={() => archive(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-amber-50 hover:text-amber-700" title="Archiver" data-testid={`archive-contact-${c.id}`}><Archive className="h-3.5 w-3.5" /></button>
                        )}
                        <button onClick={() => remove(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer définitivement" data-testid={`delete-contact-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {table.items.length === 0 && !table.loading && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-neutral-500">Aucun contact{table.q ? ` pour "${table.q}"` : ""}.</td></tr>
                )}
                {table.loading && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-neutral-400">Chargement…</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <AdminTablePager table={table} />
        </div>

        {(editing || showCreate) && (
          <ContactModal
            initial={editing || EMPTY_CONTACT}
            isEdit={!!editing}
            onClose={() => { setEditing(null); setShowCreate(false); }}
            onSaved={() => { setEditing(null); setShowCreate(false); table.reload(); }}
          />
        )}
        {converting && (
          <ConvertToMemberModal
            contact={converting}
            onClose={() => setConverting(null)}
            onSaved={() => { setConverting(null); table.reload(); }}
          />
        )}
        {linking && (
          <LinkContactModal
            contact={linking}
            onClose={() => setLinking(null)}
            onSaved={() => { setLinking(null); table.reload(); }}
          />
        )}
        {dupOpen && (
          <DuplicatesModal
            groups={dupGroups || []}
            onClose={() => setDupOpen(false)}
            onMerge={mergeDuplicates}
          />
        )}
        {passwordModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="manual-password-modal">
            <form onSubmit={submitManualPassword} className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <button type="button" onClick={() => setPasswordModal({ open: false, contact: null, password: "", notify: true })} className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100" aria-label="Fermer"><X className="h-4 w-4" /></button>
              <h2 className="text-xl font-bold text-navy"><Key className="mr-1 inline h-5 w-5 text-amber-600" /> Définir un mot de passe</h2>
              <p className="mt-1 text-xs text-neutral-600">
                Pour <strong>{passwordModal.contact?.first_name} {passwordModal.contact?.last_name}</strong> (<span className="text-wine-700">{passwordModal.contact?.email}</span>)
              </p>
              <div className="mt-5">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Nouveau mot de passe *</label>
                <input
                  type="text"
                  required
                  minLength={8}
                  maxLength={128}
                  value={passwordModal.password}
                  onChange={(e) => setPasswordModal({ ...passwordModal, password: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 font-mono text-sm outline-none focus:border-wine-300"
                  placeholder="8 caractères minimum"
                  autoFocus
                  data-testid="manual-password-input"
                />
                <button type="button" onClick={() => {
                  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
                  let p = "";
                  for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
                  setPasswordModal({ ...passwordModal, password: p });
                }} className="mt-2 text-xs font-semibold text-wine-600 hover:underline" data-testid="generate-password">
                  Générer un mot de passe sûr (12 caractères)
                </button>
              </div>
              <label className="mt-4 inline-flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={passwordModal.notify}
                  onChange={(e) => setPasswordModal({ ...passwordModal, notify: e.target.checked })}
                  className="accent-wine-500"
                  data-testid="manual-password-notify"
                />
                Notifier l&apos;utilisateur par email
              </label>
              <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                ⚠️ Communiquez ce mot de passe à l&apos;utilisateur via un canal sécurisé. Il pourra le changer ensuite depuis son profil personnel.
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setPasswordModal({ open: false, contact: null, password: "", notify: true })} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">Annuler</button>
                <button type="submit" className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600 disabled:opacity-50" disabled={passwordModal.password.length < 8} data-testid="manual-password-submit">
                  <Save className="h-4 w-4" /> Enregistrer
                </button>
              </div>
            </form>
          </div>
        )}

        {bulkEmail.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bulk-email-modal">
            <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => !bulkEmail.sending && setBulkEmail({ open: false, subject: "", html: "", sending: false, template_id: "" })} aria-label="Fermer" />
            <div className="relative w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-navy"><Send className="mr-1 inline h-5 w-5 text-wine-600" /> Emailing groupé</h2>
                  <p className="mt-0.5 text-xs text-neutral-500">Envoi à <strong className="text-wine-700">{selected.size} contact{selected.size > 1 ? "s" : ""}</strong> sélectionné{selected.size > 1 ? "s" : ""}. Le mail est branché en gabarit WineTech (bannière rose + logo + signature).</p>
                </div>
                <button onClick={() => !bulkEmail.sending && setBulkEmail({ open: false, subject: "", html: "", sending: false, template_id: "" })} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Template (optionnel)</label>
                  <select
                    value={bulkEmail.template_id}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm bg-white"
                    data-testid="bulk-email-template-select"
                    disabled={bulkEmail.sending}
                  >
                    <option value="">— Aucun (saisie libre) —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.slug} · [{(t.lang || "fr").toUpperCase()}] — {t.subject}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-neutral-500">💡 Sélectionner un template pré-remplit le sujet et le message. Vous pouvez ensuite les modifier.</p>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Sujet</label>
                  <input value={bulkEmail.subject} onChange={(e) => setBulkEmail((b) => ({ ...b, subject: e.target.value }))} placeholder="Ex: 🍷 Invitation événement WineTech Tour Languedoc" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="bulk-email-subject" disabled={bulkEmail.sending} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Message (texte libre, Markdown léger ou HTML)</label>
                  <textarea
                    value={bulkEmail.html}
                    onChange={(e) => setBulkEmail((b) => ({ ...b, html: e.target.value }))}
                    placeholder={`Bonjour {name},\n\nNous avons le plaisir de vous inviter à notre prochain événement **WineTech Tour Languedoc**.\n\nRendez-vous *jeudi 24 juin* à Narbonne.\n\nInscription : [s'inscrire ici](https://lawinetech.com/events/winetech-tour)\n\nÀ très vite,\nL'équipe La WineTech`}
                    className="mt-1 h-56 w-full resize-y rounded-xl border border-neutral-200 px-3 py-2 text-sm leading-relaxed"
                    data-testid="bulk-email-html"
                    disabled={bulkEmail.sending}
                  />
                  <p className="mt-1 text-[10px] text-neutral-500">
                    💡 Astuce — vous pouvez écrire en texte libre : les sauts de ligne sont conservés, <code>**gras**</code>, <code>*italique*</code> et <code>[lien](https://…)</code> sont reconnus. Le HTML (<code>&lt;p&gt; &lt;br&gt; &lt;strong&gt;</code>…) est aussi accepté.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <p className="text-[11px] text-neutral-500">⚠️ Les contacts archivés et sans email valide sont automatiquement exclus.</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setBulkEmail({ open: false, subject: "", html: "", sending: false, template_id: "" })} disabled={bulkEmail.sending} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-50">Annuler</button>
                  <button onClick={sendBulkEmail} disabled={bulkEmail.sending} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="bulk-email-send">
                    <Send className="h-4 w-4" /> {bulkEmail.sending ? "Envoi…" : `Envoyer à ${selected.size} contact${selected.size > 1 ? "s" : ""}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <BulkEmailModal
        open={bulkEmailModalOpen}
        onClose={() => setBulkEmailModalOpen(false)}
        entity="contacts"
        ids={Array.from(selected)}
        typeFilter="contact"
        allowSegment
        onSent={() => { setSelected(new Set()); table.reload(); }}
      />
    </PortalLayout>
  );
}

function DuplicatesModal({ groups, onClose, onMerge }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="duplicates-modal">
      <div className="relative max-h-[88vh] w-full max-w-3xl overflow-auto rounded-2xl bg-white p-6 shadow-xl">
        <button onClick={onClose} className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100" aria-label="Fermer"><X className="h-4 w-4" /></button>
        <h2 className="text-xl font-bold text-navy"><Users2 className="mr-1 inline h-5 w-5 text-amber-600" /> Doublons détectés</h2>
        <p className="mt-1 text-xs text-neutral-600">Les contacts sont groupés par email (insensible à la casse). Choisissez le contact principal puis fusionnez — les doublons seront archivés et leurs champs vides reportés sur le principal.</p>
        <div className="mt-5 space-y-4">
          {groups.length === 0 && <div className="rounded-lg bg-emerald-50 p-4 text-center text-sm text-emerald-700">Aucun doublon détecté.</div>}
          {groups.map((g, gi) => (
            <DuplicateGroup key={g.email} group={g} onMerge={onMerge} gi={gi} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DuplicateGroup({ group, onMerge, gi }) {
  const [primary, setPrimary] = useState(group.contacts[0]?.id || "");
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid={`dup-group-${gi}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-amber-900">{group.email} <span className="text-xs text-neutral-500">({group.count} contacts)</span></div>
        <button onClick={() => onMerge(group, primary)} disabled={!primary} className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-amber-700 disabled:opacity-50" data-testid={`merge-group-${gi}`}><Users2 className="h-3 w-3" /> Fusionner</button>
      </div>
      <div className="mt-3 space-y-2">
        {group.contacts.map((c) => (
          <label key={c.id} className={`flex cursor-pointer items-center justify-between rounded-lg border bg-white px-3 py-2 text-xs ${primary === c.id ? "border-wine-500 ring-1 ring-wine-500" : "border-neutral-200"}`} data-testid={`dup-row-${c.id}`}>
            <div className="flex items-center gap-2">
              <input type="radio" name={`primary-${gi}`} checked={primary === c.id} onChange={() => setPrimary(c.id)} />
              <div>
                <div className="font-semibold text-neutral-800">{c.first_name} {c.last_name}</div>
                <div className="text-[10px] text-neutral-500">{c.source || "—"} · {c.company_name || "Sans entreprise"}{c.created_at ? ` · créé le ${new Date(c.created_at).toLocaleDateString("fr")}` : ""}</div>
              </div>
            </div>
            {primary === c.id && <span className="rounded-full bg-wine-100 px-2 py-0.5 text-[10px] font-bold text-wine-700">Principal</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

function ContactModal({ initial, isEdit, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [entityQuery, setEntityQuery] = useState("");
  const [entityResults, setEntityResults] = useState({ members: [], prospects: [] });
  const [entitySearching, setEntitySearching] = useState(false);
  const [segmentGroups, setSegmentGroups] = useState([]); // canonical taxonomy for the tag picker
  const [tagQuery, setTagQuery] = useState(""); // Sprint 92n — tag combobox filter
  const [extraTags, setExtraTags] = useState([]); // locally-created tags (not yet persisted globally)

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/contacts/segments");
        setSegmentGroups(r.data?.groups || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const toggleTag = (value) => {
    const tags = Array.isArray(form.tags) ? [...form.tags] : [];
    const idx = tags.indexOf(value);
    if (idx >= 0) tags.splice(idx, 1); else tags.push(value);
    setForm({ ...form, tags });
  };

  // Sprint 92n — Unified tag picker helpers
  const normalizeTag = (raw) => (raw || "").toString().trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9\-_:]/g, "");

  // Existing tags visible in groups (canonical + custom from server + locally created)
  const allExistingTagValues = (() => {
    const set = new Set();
    (segmentGroups || []).forEach((g) => (g.values || []).forEach((v) => set.add(v.value)));
    (extraTags || []).forEach((v) => set.add(v));
    return set;
  })();

  const matchingExistingTag = (() => {
    const q = normalizeTag(tagQuery);
    if (!q) return null;
    return allExistingTagValues.has(q) ? q : null;
  })();

  const visibleGroups = (() => {
    const q = (tagQuery || "").trim().toLowerCase();
    const base = [...(segmentGroups || [])];
    // Inject a synthetic group for locally-created tags so they render as pills
    if (extraTags.length > 0) {
      const existingCustomIdx = base.findIndex((g) => g.key === "custom");
      const newlyCreatedValues = extraTags.map((v) => ({ value: v, label: v, count: 0 }));
      if (existingCustomIdx >= 0) {
        const merged = [...base[existingCustomIdx].values];
        newlyCreatedValues.forEach((nv) => { if (!merged.find((x) => x.value === nv.value)) merged.push(nv); });
        base[existingCustomIdx] = { ...base[existingCustomIdx], values: merged };
      } else {
        base.push({ key: "custom", label: "Tags personnalisés", values: newlyCreatedValues });
      }
    }
    if (!q) return base;
    return base
      .map((g) => ({
        ...g,
        values: (g.values || []).filter((v) =>
          (v.value || "").toLowerCase().includes(q) || (v.label || "").toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.values.length > 0);
  })();

  const createAndAddTag = () => {
    const clean = normalizeTag(tagQuery);
    if (!clean) return;
    if (!allExistingTagValues.has(clean)) {
      setExtraTags((prev) => (prev.includes(clean) ? prev : [...prev, clean]));
    }
    const tags = Array.isArray(form.tags) ? [...form.tags] : [];
    if (!tags.includes(clean)) tags.push(clean);
    setForm({ ...form, tags });
    setTagQuery("");
    toast.success(`Tag « ${clean} » ajouté`);
  };

  // Search both Members and Prospects when query changes (debounced)
  useEffect(() => {
    const q = entityQuery.trim();
    const t = setTimeout(async () => {
      if (q.length < 2) { setEntityResults({ members: [], prospects: [] }); setEntitySearching(false); return; }
      setEntitySearching(true);
      try {
        const [m, p] = await Promise.all([
          api.get("/admin/companies", { params: { q, limit: 8 } }),
          api.get("/admin/prospects", { params: { q, limit: 8 } }),
        ]);
        const mArr = Array.isArray(m.data) ? m.data : (m.data?.items || []);
        const pArr = Array.isArray(p.data) ? p.data : (p.data?.items || []);
        setEntityResults({ members: mArr.slice(0, 8), prospects: pArr.slice(0, 8) });
      } catch { setEntityResults({ members: [], prospects: [] }); }
      finally { setEntitySearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [entityQuery]);

  const pickMember = (m) => {
    setForm({ ...form, company_id: m.id, company_name: m.name, linked_company_id: m.id, linked_prospect_id: null, pole: m.pole || form.pole });
    setEntityQuery(""); setEntityResults({ members: [], prospects: [] });
    toast.success(`Lié au membre : ${m.name}`);
  };
  const pickProspect = (p) => {
    setForm({ ...form, company_name: p.company_name || form.company_name, linked_prospect_id: p.id, linked_company_id: null, company_id: null, pole: p.pole || form.pole });
    setEntityQuery(""); setEntityResults({ members: [], prospects: [] });
    toast.success(`Lié au prospect : ${p.company_name || p.contact_email}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/contacts/${initial.id}`, form);
        toast.success("Contact modifié");
      } else {
        await api.post("/admin/contacts", form);
        toast.success("Contact créé");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="contact-modal">
        <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-4">
          <h2 className="text-xl font-bold text-navy">{isEdit ? "Modifier le contact" : "Nouveau contact"}</h2>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
        {!isEdit && (
          <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">🔎 Recherche INSEE (auto-remplit l&apos;entreprise)</div>
            <CompanyAutocomplete
              placeholder="Tapez un nom d'entreprise ou un SIREN…"
              onSelect={(r) => { if (r) setForm((f) => ({ ...f, company_name: r.denomination || f.company_name })); }}
            />
          </div>
        )}

        {/* Lier à un membre ou un prospect existant */}
        <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3" data-testid="link-entity-block">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">🔗 Lier à un membre ou un prospect existant</div>
            {(form.linked_company_id || form.linked_prospect_id) && (
              <button
                type="button"
                onClick={() => setForm({ ...form, linked_company_id: null, linked_prospect_id: null, company_id: null })}
                className="text-[10px] font-semibold text-rose-600 hover:underline"
                data-testid="unlink-entity-btn"
              >Délier</button>
            )}
          </div>
          {(form.linked_company_id || form.linked_prospect_id) ? (
            <div className="rounded-lg bg-white px-3 py-2 text-xs text-indigo-800" data-testid="linked-entity-summary">
              {form.linked_company_id ? (
                <span><Crown className="mr-1 inline h-3 w-3" /> Membre : <strong>{form.company_name}</strong></span>
              ) : (
                <span><Building2 className="mr-1 inline h-3 w-3" /> Prospect : <strong>{form.company_name || "—"}</strong></span>
              )}
            </div>
          ) : (
            <>
              <input
                value={entityQuery}
                onChange={(e) => setEntityQuery(e.target.value)}
                placeholder="Tapez 2+ caractères pour chercher dans les membres et prospects…"
                className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
                data-testid="link-entity-search"
              />
              {entitySearching && <div className="mt-1 text-[10px] text-indigo-500">Recherche…</div>}
              {(entityResults.members.length > 0 || entityResults.prospects.length > 0) && (
                <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-indigo-100 bg-white">
                  {entityResults.members.length > 0 && (
                    <div>
                      <div className="bg-wine-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-wine-700">👑 Membres ({entityResults.members.length})</div>
                      {entityResults.members.map((m) => (
                        <button key={m.id} type="button" onClick={() => pickMember(m)} className="block w-full border-t border-neutral-50 px-3 py-2 text-left text-xs hover:bg-wine-50/60" data-testid={`pick-member-${m.id}`}>
                          <div className="font-semibold text-neutral-900">{m.name}</div>
                          <div className="text-[10px] text-neutral-500">{m.city || ""}{m.pole ? ` · ${m.pole}` : ""}{m.category ? ` · ${m.category}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {entityResults.prospects.length > 0 && (
                    <div>
                      <div className="bg-indigo-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700">🎯 Prospects ({entityResults.prospects.length})</div>
                      {entityResults.prospects.map((p) => (
                        <button key={p.id} type="button" onClick={() => pickProspect(p)} className="block w-full border-t border-neutral-50 px-3 py-2 text-left text-xs hover:bg-indigo-50/60" data-testid={`pick-prospect-${p.id}`}>
                          <div className="font-semibold text-neutral-900">{p.company_name || p.contact_name || "—"}</div>
                          <div className="text-[10px] text-neutral-500">{p.contact_email || ""}{p.status ? ` · ${p.status}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {entityQuery.length >= 2 && !entitySearching && entityResults.members.length === 0 && entityResults.prospects.length === 0 && (
                <div className="mt-2 rounded-lg bg-white px-3 py-2 text-[11px] italic text-neutral-500">Aucun membre ni prospect trouvé pour « {entityQuery} ».</div>
              )}
            </>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-first-name" />
          <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-last-name" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" type="email" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-email" />
          <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-phone" />
          <input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="WhatsApp (ex: +33612345678)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-whatsapp" />
          <input value={form.job_title || ""} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Fonction" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-job-title" />
          <input value={form.linkedin || ""} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="URL LinkedIn" type="url" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-linkedin" />
          <input value={form.calendar_link || ""} onChange={(e) => setForm({ ...form, calendar_link: e.target.value })} placeholder="Lien calendrier (Calendly, Google…)" type="url" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-calendar" />
          {/* Sprint 92j — Company/pole are inherited from the linked entity (see picker above).
              If the contact isn't linked (rare: prospect only known by name), we keep a fallback text field. */}
          {!(form.linked_company_id || form.linked_prospect_id) && (
            <input value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Entreprise (si aucun lien membre/prospect)" className="rounded-xl border border-dashed border-neutral-300 px-3 py-2 text-sm text-neutral-600" data-testid="contact-company-name-fallback" />
          )}
          {(form.linked_company_id && form.pole) && (
            <div className="flex items-center gap-2 rounded-xl bg-wine-50 px-3 py-2 text-xs text-wine-800" data-testid="inherited-pole">
              <Building2 className="h-3.5 w-3.5" />
              <span>Pôle : <strong>{poleLabel(form.pole)}</strong> <span className="text-[10px] text-wine-500">(hérité de l&apos;entreprise)</span></span>
            </div>
          )}
          {!isEdit && (
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          )}
          {/* Sprint 92n — Unified tag picker: canonical taxonomy + custom tags + inline creation.
              Selected pills shown at the top with ✕, search input to filter, and "Créer le tag" button
              when the query doesn't match any existing tag. */}
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-3 sm:col-span-2" data-testid="contact-tags-picker">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Tags &amp; Segments</div>
              <div className="text-[10px] text-indigo-500">Cliquer pour ajouter/retirer · tapez pour rechercher ou créer</div>
            </div>

            {/* Selected pills */}
            <div className="mb-2 flex flex-wrap gap-1.5 min-h-[28px]" data-testid="selected-tags-row">
              {(form.tags || []).length === 0 ? (
                <span className="text-[11px] italic text-neutral-400">Aucun tag pour le moment</span>
              ) : (
                (form.tags || []).map((t) => {
                  const label = tagLabelFor(t, segmentGroups, extraTags);
                  return (
                    <span key={t} className="inline-flex items-center gap-1 rounded-full bg-wine-100 border border-wine-300 px-2 py-0.5 text-[11px] font-semibold text-wine-800" data-testid={`selected-tag-${t}`}>
                      {label}
                      <button type="button" onClick={() => toggleTag(t)} className="hover:text-wine-950" title="Retirer" data-testid={`remove-tag-${t}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })
              )}
            </div>

            {/* Search / create input */}
            <div className="mb-2 flex items-center gap-2">
              <input
                type="text"
                value={tagQuery}
                onChange={(e) => setTagQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && tagQuery.trim() && !matchingExistingTag) {
                    e.preventDefault();
                    createAndAddTag();
                  }
                }}
                placeholder="Rechercher un tag existant ou en créer un nouveau…"
                className="flex-1 rounded-xl border border-neutral-200 px-3 py-1.5 text-sm"
                data-testid="tag-search-input"
              />
              {tagQuery.trim() && !matchingExistingTag && (
                <button
                  type="button"
                  onClick={createAndAddTag}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700"
                  data-testid="create-tag-btn"
                >
                  <Tag className="h-3 w-3" /> Créer « {normalizeTag(tagQuery)} »
                </button>
              )}
            </div>

            {/* Grouped existing tags (filtered by query) */}
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {visibleGroups.length === 0 && (
                <div className="text-[11px] italic text-neutral-400 py-2 text-center">Aucun tag ne correspond à « {tagQuery} »</div>
              )}
              {visibleGroups.map((g) => (
                <div key={g.key}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">{g.label}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {g.values.map((v) => {
                      const active = (form.tags || []).includes(v.value);
                      return (
                        <button
                          key={v.value}
                          type="button"
                          onClick={() => toggleTag(v.value)}
                          className={`rounded-full border px-2 py-0.5 text-[11px] transition ${active ? "border-wine-400 bg-wine-100 text-wine-800 font-semibold" : "border-neutral-200 bg-white text-neutral-600 hover:border-wine-200 hover:text-wine-700"}`}
                          data-testid={`segment-toggle-${v.value}`}
                        >
                          {v.label}{typeof v.count === "number" && v.count > 0 ? ` (${v.count})` : ""}{active ? " ✓" : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <select value={form.legal_basis || ""} onChange={(e) => setForm({ ...form, legal_basis: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="contact-legal-basis" title="Base légale RGPD pour le traitement des données">
            <option value="">— Base légale RGPD —</option>
            <option value="contract">📄 Informations de contrat</option>
            <option value="consent">✅ Consentement</option>
            <option value="execution_contract">⚖️ Exécution d&apos;un contrat</option>
            <option value="legal_obligation">📜 Obligation légale</option>
          </select>
          <input value={form.consent_source || ""} onChange={(e) => setForm({ ...form, consent_source: e.target.value })} placeholder="Source du consentement / contexte (ex: Newsletter)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={3} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" />
        </div>

        <div className="mt-4 rounded-xl border border-neutral-100 bg-neutral-50/40 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-neutral-700">Visibilité par champ</div>
          <p className="mt-1 text-[11px] text-neutral-500">Qui peut voir chaque information sur la fiche entreprise publique ?</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {[
              { key: "visibility_email", label: "Email" },
              { key: "visibility_phone", label: "Téléphone" },
              { key: "visibility_whatsapp", label: "WhatsApp" },
              { key: "visibility_linkedin", label: "LinkedIn" },
              { key: "visibility_calendar", label: "Calendrier" },
            ].map((f) => (
              <label key={f.key} className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium text-neutral-700">{f.label}</span>
                <select value={form[f.key] || "members"} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs" data-testid={`contact-${f.key}`}>
                  {VIS_LEVELS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-neutral-100 bg-white px-6 py-3">
          <button onClick={onClose} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50">Annuler</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="save-contact-btn">
            <Save className="h-3.5 w-3.5" /> {saving ? "Sauvegarde…" : (isEdit ? "Modifier" : "Créer")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConvertToMemberModal({ contact, onClose, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [fees, setFees] = useState([]);
  const [form, setForm] = useState({
    category_id: contact.category || "",
    pole: contact.pole || "",
    bracket: 1,
    final_amount: 0,
    payment_link: true,
    skip_email: false,
    custom_message: "",
    // Billing
    annual_revenue: "",
    legal_name: "",
    siren: "",
    vat_number: "",
    billing_address: "",
    billing_postal_code: "",
    billing_city: "",
    billing_country: "France",
  });
  const [showBilling, setShowBilling] = useState(false);
  const [autoCalc, setAutoCalc] = useState(null);  // { bracket, bracket_label, fee }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/public/membership-pricing").then((r) => {
      const d = r.data || [];
      setCategories(d);
      const matchingFees = (d.find((c) => c.id === form.category_id)?.fees) || null;
      setFees(matchingFees ? [matchingFees] : []);
      if (matchingFees) setForm((f) => ({ ...f, final_amount: matchingFees[`fee_${f.bracket}`] || 0 }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cat = categories.find((c) => c.id === form.category_id);
    if (cat?.fees) {
      setFees([cat.fees]);
      const fee = cat.fees[`fee_${form.bracket}`] || 0;
      setForm((f) => ({ ...f, final_amount: fee }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category_id, form.bracket, categories]);

  // Auto-calcul prix suggéré quand CA + catégorie sont renseignés
  useEffect(() => {
    if (!form.category_id || !form.annual_revenue) {
      setAutoCalc(null);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/public/simulate-fee", {
          category_id: form.category_id,
          revenue: parseFloat(form.annual_revenue) || 0,
        }, { signal: ctrl.signal });
        setAutoCalc(r.data);
        // Auto-apply suggested bracket + fee
        setForm((f) => ({ ...f, bracket: r.data.bracket, final_amount: r.data.fee }));
      } catch { /* ignore */ }
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [form.annual_revenue, form.category_id]);

  const currentFee = fees[0] || null;
  const bracketsAvailable = currentFee ? [1, 2, 3, 4].filter((i) => currentFee[`bracket_${i}_label`]) : [1];

  const convert = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        annual_revenue: form.annual_revenue ? parseFloat(form.annual_revenue) : null,
      };
      const r = await api.post(`/admin/contacts/${contact.id}/convert-to-member`, payload);
      setResult(r.data);
      toast.success("Conversion réussie ! Application créée.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur conversion");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="convert-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy">
            <Crown className="mr-1 inline h-5 w-5 text-purple-600" />
            Convertir {contact.first_name} {contact.last_name} en membre
          </h2>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        {!result ? (
          <>
            <div className="mb-4 rounded-xl bg-neutral-50 p-3 text-xs text-neutral-700">
              <strong>{contact.email}</strong> · {contact.company_name || "Entreprise non renseignée"}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Catégorie d&apos;adhésion *</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value, bracket: 1 })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="convert-category">
                  <option value="">— Choisir —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {currentFee && (
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">CA annuel déclaré (€) — calcule la tranche automatiquement</label>
                  <input
                    type="number" min="0" step="1000"
                    value={form.annual_revenue}
                    onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })}
                    placeholder="Ex: 250000"
                    className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    data-testid="convert-revenue"
                  />
                  {autoCalc && (
                    <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900" data-testid="convert-auto-suggest">
                      <strong>✨ Suggestion auto :</strong> Tranche {autoCalc.bracket} ({autoCalc.bracket_label}) → <strong>{autoCalc.fee} €</strong>
                    </div>
                  )}
                </div>
              )}
              {currentFee && (
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Tranche tarifaire (override possible)</label>
                  <div className="flex flex-wrap gap-2">
                    {bracketsAvailable.map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => setForm({ ...form, bracket: b })}
                        className={`rounded-xl border px-3 py-2 text-xs font-bold ${form.bracket === b ? "border-wine-500 bg-wine-50 text-wine-800" : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"}`}
                        data-testid={`convert-bracket-${b}`}
                      >
                        {currentFee[`bracket_${b}_label`]} · {currentFee[`fee_${b}`]} €
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Montant final (€)</label>
                <input type="number" min="0" step="10" value={form.final_amount} onChange={(e) => setForm({ ...form, final_amount: parseFloat(e.target.value) || 0 })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="convert-amount" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Pôle sectoriel</label>
                <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                  <option value="">— Pôle —</option>
                  {POLES.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Message personnalisé (dans l&apos;email)</label>
                <textarea rows={3} value={form.custom_message} onChange={(e) => setForm({ ...form, custom_message: e.target.value })} placeholder="Ex: Suite à notre échange du..." className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="convert-message" />
              </div>

              {/* Billing section (collapsible) */}
              <div className="sm:col-span-2 rounded-xl border border-neutral-200 bg-neutral-50/40">
                <button type="button" onClick={() => setShowBilling(!showBilling)} className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-bold text-neutral-700" data-testid="toggle-billing">
                  <span>📋 Informations de facturation (SIREN, TVA, adresse)</span>
                  <span>{showBilling ? "▲" : "▼"}</span>
                </button>
                {showBilling && (
                  <div className="grid gap-2 border-t border-neutral-200 p-3 sm:grid-cols-2" data-testid="billing-fields">
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-emerald-700">🔎 Recherche INSEE (autocomplète tous les champs)</label>
                      <CompanyAutocomplete
                        placeholder="Tapez nom ou SIREN — données data.gouv.fr"
                        onSelect={(r) => {
                          if (!r) return;
                          setForm((f) => ({
                            ...f,
                            legal_name: r.denomination || f.legal_name,
                            siren: r.siren || f.siren,
                            vat_number: r.vat_number || f.vat_number,
                            billing_address: r.address || f.billing_address,
                            billing_postal_code: r.postal_code || f.billing_postal_code,
                            billing_city: r.city || f.billing_city,
                            billing_country: r.country || f.billing_country,
                          }));
                        }}
                      />
                    </div>
                    <input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Raison sociale" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs sm:col-span-2" data-testid="billing-legal-name" />
                    <input value={form.siren} onChange={(e) => setForm({ ...form, siren: e.target.value })} placeholder="SIREN (9 chiffres)" maxLength={9} className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" data-testid="billing-siren" />
                    <input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="N° TVA (ex: FR12345678901)" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" data-testid="billing-vat" />
                    <input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} placeholder="Adresse" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs sm:col-span-2" />
                    <input value={form.billing_postal_code} onChange={(e) => setForm({ ...form, billing_postal_code: e.target.value })} placeholder="Code postal" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" />
                    <input value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} placeholder="Ville" className="rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" />
                    <CountrySelect value={form.billing_country} onSelect={({ name, lang }) => setForm({ ...form, billing_country: name, lang })} className="text-xs sm:col-span-2 px-2 py-1.5" testId="contact-billing-country" />
                    <div className="sm:col-span-2 text-[10px] text-neutral-500 italic">Source : API publique data.gouv.fr (Sirene v3). Stripe collecte aussi automatiquement adresse + TVA UE lors du paiement.</div>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 text-xs text-neutral-700 sm:col-span-2">
                <input type="checkbox" checked={form.payment_link} onChange={(e) => setForm({ ...form, payment_link: e.target.checked })} data-testid="convert-payment-toggle" />
                <span>Inclure un <strong>lien de paiement Stripe</strong> dans l&apos;email (recommandé si montant &gt; 0)</span>
              </label>
              <label className="flex items-center gap-2 text-xs text-neutral-700 sm:col-span-2">
                <input type="checkbox" checked={form.skip_email} onChange={(e) => setForm({ ...form, skip_email: e.target.checked })} data-testid="convert-skip-email" />
                <span>Ne pas envoyer d&apos;email (création silencieuse)</span>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50">Annuler</button>
              <button onClick={convert} disabled={submitting || !form.category_id} className="inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-5 py-2 text-xs font-bold text-white hover:bg-purple-700 disabled:opacity-50" data-testid="convert-submit-btn">
                <Crown className="h-3.5 w-3.5" /> {submitting ? "Conversion…" : "Convertir en membre"}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center" data-testid="convert-result">
            <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-emerald-50"><Crown className="h-7 w-7 text-emerald-600" /></div>
            <h3 className="text-lg font-bold text-emerald-700">Conversion réussie</h3>
            <p className="mt-2 text-sm text-neutral-600">Application <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs">{result.application_id?.slice(0, 8)}</code> créée pour <strong>{result.amount} €</strong>.</p>
            {result.email_scheduled && <p className="mt-1 text-xs text-neutral-500">📧 Email d&apos;invitation envoyé à {contact.email}</p>}
            {result.payment_url && (
              <a href={result.payment_url} target="_blank" rel="noreferrer" className="mt-4 inline-block rounded-full bg-purple-600 px-5 py-2 text-xs font-bold text-white hover:bg-purple-700" data-testid="convert-stripe-link">
                Ouvrir le lien de paiement Stripe →
              </a>
            )}
            <div className="mt-5">
              <button onClick={onSaved} className="rounded-full border border-neutral-300 bg-white px-5 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="convert-close-btn">Fermer</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LinkContactModal({ contact, onClose, onSaved }) {
  const [prospects, setProspects] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(contact.linked_prospect_id ? "prospect" : (contact.company_id ? "member" : "prospect"));
  const [selected, setSelectedItem] = useState({
    prospect_id: contact.linked_prospect_id || "",
    company_id: contact.company_id || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/prospects").then((r) => setProspects(r.data || [])).catch(() => {});
    api.get("/admin/companies?per_page=200").then((r) => {
      const data = r.data;
      setMembers(Array.isArray(data) ? data : (data.items || []));
    }).catch(() => {});
  }, []);

  const filtered = (list, key) => list.filter((x) => {
    const s = search.toLowerCase();
    if (!s) return true;
    return (x.name || x.company_name || "").toLowerCase().includes(s) ||
           (x.contact_name || "").toLowerCase().includes(s) ||
           (x.contact_email || "").toLowerCase().includes(s);
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = tab === "prospect"
        ? { prospect_id: selected.prospect_id || null, company_id: null }
        : { company_id: selected.company_id || null, prospect_id: null };
      await api.post(`/admin/contacts/${contact.id}/link`, body);
      toast.success("Contact lié avec succès");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="link-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy"><Link2 className="mr-1 inline h-5 w-5 text-indigo-600" /> Lier {contact.first_name} {contact.last_name}</h2>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="mb-3 flex gap-1 rounded-xl bg-neutral-100 p-1">
          <button onClick={() => setTab("prospect")} className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-bold ${tab === "prospect" ? "bg-white text-indigo-700 shadow-sm" : "text-neutral-600"}`} data-testid="tab-prospect">Prospect ({prospects.length})</button>
          <button onClick={() => setTab("member")} className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-bold ${tab === "member" ? "bg-white text-indigo-700 shadow-sm" : "text-neutral-600"}`} data-testid="tab-member">Membre ({members.length})</button>
        </div>

        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="mb-3 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="link-search" />

        <div className="max-h-64 overflow-y-auto rounded-xl border border-neutral-100">
          {tab === "prospect" ? (
            <>
              <button onClick={() => setSelectedItem({ ...selected, prospect_id: "" })} className={`flex w-full items-center justify-between border-b border-neutral-100 px-3 py-2 text-left text-xs hover:bg-neutral-50 ${!selected.prospect_id ? "bg-indigo-50 text-indigo-700 font-bold" : ""}`}>
                <span>— Aucun prospect —</span>
              </button>
              {filtered(prospects, "p").map((p) => (
                <button key={p.id} onClick={() => setSelectedItem({ ...selected, prospect_id: p.id })} className={`flex w-full items-center justify-between border-b border-neutral-100 px-3 py-2 text-left text-xs hover:bg-neutral-50 ${selected.prospect_id === p.id ? "bg-indigo-50 text-indigo-700 font-bold" : ""}`} data-testid={`prospect-opt-${p.id}`}>
                  <div>
                    <div className="font-semibold">{p.company_name || "—"}</div>
                    <div className="text-[10px] text-neutral-500">{p.contact_name} · {p.contact_email}</div>
                  </div>
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[9px] font-bold">{p.status}</span>
                </button>
              ))}
              {prospects.length === 0 && <div className="px-4 py-6 text-center text-xs text-neutral-400">Aucun prospect</div>}
            </>
          ) : (
            <>
              <button onClick={() => setSelectedItem({ ...selected, company_id: "" })} className={`flex w-full items-center justify-between border-b border-neutral-100 px-3 py-2 text-left text-xs hover:bg-neutral-50 ${!selected.company_id ? "bg-indigo-50 text-indigo-700 font-bold" : ""}`}>
                <span>— Aucune entreprise —</span>
              </button>
              {filtered(members, "m").map((m) => (
                <button key={m.id} onClick={() => setSelectedItem({ ...selected, company_id: m.id })} className={`flex w-full items-center justify-between border-b border-neutral-100 px-3 py-2 text-left text-xs hover:bg-neutral-50 ${selected.company_id === m.id ? "bg-indigo-50 text-indigo-700 font-bold" : ""}`} data-testid={`member-opt-${m.id}`}>
                  <div>
                    <div className="font-semibold">{m.name}</div>
                    <div className="text-[10px] text-neutral-500">{m.city}{m.pole ? ` · ${poleLabel(m.pole)}` : ""}</div>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-700">{m.status}</span>
                </button>
              ))}
              {members.length === 0 && <div className="px-4 py-6 text-center text-xs text-neutral-400">Aucun membre</div>}
            </>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50">Annuler</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-5 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50" data-testid="save-link-btn">
            <Link2 className="h-3.5 w-3.5" /> {saving ? "Sauvegarde…" : "Enregistrer le lien"}
          </button>
        </div>
      </div>
    </div>
  );
}
